import {useOverlayContext} from "../../contexts/OverlayContext";
import {FiDownload} from "react-icons/fi";
import * as XLSX from "xlsx";
import React from "react";
import "../../css/ui.css";


const formatPValue = (value: number): string =>
  Math.abs(value) < 0.001 ? value.toExponential(4) : String(value);

const CANONICAL_SHEET_DESCRIPTIONS: Record<string, string> = {
  "NEG_qGI": "Negative genetic interaction enrichment",
  "POS_qGI": "Positive genetic interaction enrichment",
};

const buildREADME = (overlayType: string | null | undefined, sheetNames: string[]): XLSX.WorkSheet => {
  const type = overlayType?.toUpperCase();
  const sheetLines = sheetNames.map(name =>
    name in CANONICAL_SHEET_DESCRIPTIONS ? [`${name}: ${CANONICAL_SHEET_DESCRIPTIONS[name]}`] : [name]
  );
  const aoa = [
    [`Dataset: ${type} Overlay Results`],
    [],
    ["Sheets:"],
    ...sheetLines,
    [],
    ["Structure:"],
    ["Columns: Enriched biological processes (domains and p-values)"],
    ["Rows: Genes associated with each domain"],
    ["Genes may appear in multiple domains"],
    [],
    ["Statistics:"],
    ["Test: Right-tailed hypergeometric"],
    ["Threshold: p < 0.001"],
    [],
    ["Interpretation:"],
    ["SAFE computes enrichment at the gene (node) level"],
    ["RISK computes enrichment at the cluster (community) level"],
    ["In RISK, all genes within a cluster share the same p-value"],
    ["In SAFE, p-values are specific to individual genes"],
    ["Multiple p-values within a domain reflect contributions from distinct genes (SAFE) or clusters (RISK)"],
  ];
  return XLSX.utils.aoa_to_sheet(aoa);
};

const buildQGISheet = (
  enrichedDomains: Record<string, string[]>,
  pvalueMap: Map<string, number>
): XLSX.WorkSheet => {
  const allDomains = Object.keys(enrichedDomains).sort((a, b) => a.localeCompare(b));

  const filteredDomains: string[] = [];
  const filteredColumns: string[][] = [];

  allDomains.forEach(domain => {
    const genes = enrichedDomains[domain]
      .filter(gene => pvalueMap.has(gene))
      .sort((a, b) => {
        const pa = pvalueMap.get(a)!;
        const pb = pvalueMap.get(b)!;
        if (pa !== pb) return pa - pb;
        return a.localeCompare(b);
      });

    if (genes.length > 0) {
      filteredDomains.push(domain);
      filteredColumns.push(genes.map(gene => gene));
      filteredDomains.push(`${domain} (p-value)`);
      filteredColumns.push(genes.map(gene => formatPValue(pvalueMap.get(gene)!)));
    }
  });

  if (filteredDomains.length === 0) {
    return XLSX.utils.aoa_to_sheet([["No enriched domains"]]);
  }

  const maxLen = Math.max(...filteredColumns.map(col => col.length));
  const aoa: string[][] = [
    filteredDomains,
    ...Array.from({length: maxLen}, (_, i) =>
      filteredColumns.map(col => col[i] ?? "")
    ),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = filteredDomains.map(name => ({wch: Math.max(name.length, 20) + 2}));
  return ws;
};

const CANONICAL_SHEET_NAMES: Record<string, string> = {
  negative: "NEG_qGI",
  positive: "POS_qGI",
};

const sanitizeSheetName = (name: string): string =>
  name.replace(/[:\\/?*[\]]/g, "").slice(0, 31) || "Sheet";

const deduplicateSheetNames = (names: string[]): string[] => {
  const seen = new Set<string>(["README"]);
  return names.map(name => {
    if (!seen.has(name)) {
      seen.add(name);
      return name;
    }
    let n = 2;
    let candidate: string;
    do {
      const suffix = `_${n++}`;
      candidate = `${name.slice(0, 31 - suffix.length)}${suffix}`;
    } while (seen.has(candidate));
    seen.add(candidate);
    return candidate;
  });
};

const OverlayDownload = () => {
  const {overlayResults, overlayType, overlayScreen} = useOverlayContext();

  const handleOverlayDownload = () => {
    const enrichedDomains = overlayResults?.enrichment?.enriched_domains;
    if (!enrichedDomains || Object.keys(enrichedDomains).length === 0) return;

    const overlay = overlayResults?.overlay ?? [];

    const sheetNames = deduplicateSheetNames(overlay.map(
      item => CANONICAL_SHEET_NAMES[item.name] ?? sanitizeSheetName(item.verbose_name)
    ));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, buildREADME(overlayType, sheetNames), "README");

    overlay.forEach((item, i) => {
      const pvalueMap = new Map(item.genes.map(g => [g.name, g.pvalue]));
      XLSX.utils.book_append_sheet(workbook, buildQGISheet(enrichedDomains, pvalueMap), sheetNames[i]);
    });

    XLSX.writeFile(
      workbook,
      `${overlayType?.toUpperCase()}${overlayScreen ? `_${overlayScreen}` : ""}_Results.xlsx`,
      {cellStyles: true}
    );
  };

  return (
    <button
      onClick={handleOverlayDownload}
      className="btn btn-outline-light"
      aria-label="Download Overlay Results"
      title="Download Overlay Results"
    >
      <FiDownload className="ui-icon"/>
    </button>
  );
};

export default OverlayDownload;
